import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { channelUrl } from '../utils/channelUrl';
import { sortByColumn } from '../utils/sorting';
import { rememberSelectedChannel } from '../utils/selectedChannel';
import { domainSearchUrl } from '../utils/storyLinks';
import { SortTh } from '../components/SortTh';
import type { AdminStory, AdminStoryDetail, StoryElement, FilterBarState } from '../types';
import { StatusMessage } from '../components/StatusMessage';
import { ScoreBreakdownModal } from '../components/ScoreBreakdownModal';
import { ArticleFilterBar } from '../components/ArticleFilterBar';
import { ReaderNavbar } from '../components/ReaderNavbar';
import {
  adminArticleFilterConfig,
  readerArticleFilterConfig,
  DEFAULT_ADMIN_FILTER_STATE,
  DEFAULT_READER_FILTER_STATE,
} from '../components/articleSearchConfigs';
import {
  describeSearch,
  filterStateToParams,
  paramsToFilterState,
  textFilterValue,
  withTextFilter,
} from '../utils/searchState';
import { decodeHtmlEntities } from '../utils/html';
import { readSocialComponent } from '../utils/baseScoreDetails';
import { formatDateTime } from '../utils/formatTime';
import { truncate } from '../utils/truncate';
import { useUser } from '../providers/UserProvider';
import { useChannels } from '../providers/ChannelProvider';
import * as adminStoryRepository from '../repositories/adminStoryRepository';
import { searchStories as readerSearchStories } from '../repositories/storyRepository';
import { safeHref } from '../utils/html';

const RESULT_LIMIT = 100;
const BREAKDOWN_STORAGE_KEY = 'search_show_score_breakdown';

export default function StoriesPage({ hideShell = false }: { hideShell?: boolean } = {}) {
  const { user, logout, isAdmin } = useUser();
  useChannels();
  const admin = isAdmin;
  const navigate = useNavigate();

  const handleSelectChannel = (id: string) => {
    rememberSelectedChannel(id);
    navigate(channelUrl(id));
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const [stories, setStories] = useState<AdminStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ message: '', isError: false });
  const searchInputRef = useRef<HTMLInputElement>(null);
  const requestSeqRef = useRef(0);

  const filterConfig = admin ? adminArticleFilterConfig : readerArticleFilterConfig;
  const defaultFilterState = admin ? DEFAULT_ADMIN_FILTER_STATE : DEFAULT_READER_FILTER_STATE;

  const [filterState, setFilterState] = useState<FilterBarState>(
    () => paramsToFilterState(searchParams, defaultFilterState) ?? defaultFilterState,
  );
  const [appliedFilterState, setAppliedFilterState] = useState(filterState);
  const [searchText, setSearchText] = useState(() => textFilterValue(filterState));

  const [showBreakdown, setShowBreakdown] = useState(
    () => localStorage.getItem(BREAKDOWN_STORAGE_KEY) === '1',
  );
  const toggleBreakdown = () => {
    setShowBreakdown((prev) => {
      localStorage.setItem(BREAKDOWN_STORAGE_KEY, prev ? '0' : '1');
      return !prev;
    });
  };

  const loadStories = async (state: FilterBarState) => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    setStatus({ message: '', isError: false });
    try {
      const query = {
        filters: state.filters.map(({ field, operator, value }) => ({ field, operator, value })),
        order_by: state.orderBy,
        order_dir: state.orderDir,
        limit: RESULT_LIMIT,
      };
      const results = admin
        ? await adminStoryRepository.filterStories(query)
        : await readerSearchStories(query) as unknown as AdminStory[];
      if (seq !== requestSeqRef.current) return;
      setStories(results);
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      setStatus({ message: (err as Error).message || 'Failed to load articles', isError: true });
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  };

  // Only submitted state loads results. Editing the query remains local so a
  // slow search cannot be launched for every intermediate keystroke/filter.
  useEffect(() => {
    loadStories(appliedFilterState);
    setSearchParams(filterStateToParams(appliedFilterState, defaultFilterState), { replace: true });
  }, [appliedFilterState]);

  // Adopt search state arriving via the URL (in-app links to /search?…,
  // back/forward). Compares serialized forms so our own URL sync above is a
  // no-op. Reads submitted state through a ref: draft edits must not be
  // overwritten before the user explicitly submits them.
  const appliedFilterStateRef = useRef(appliedFilterState);
  appliedFilterStateRef.current = appliedFilterState;
  useEffect(() => {
    const fromUrl = paramsToFilterState(searchParams, defaultFilterState) ?? defaultFilterState;
    const currentSerialized = filterStateToParams(appliedFilterStateRef.current, defaultFilterState).toString();
    if (filterStateToParams(fromUrl, defaultFilterState).toString() !== currentSerialized) {
      setFilterState(fromUrl);
      setSearchText(textFilterValue(fromUrl));
      setAppliedFilterState(fromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Keep the search box in sync when the text filter changes from elsewhere
  // (preset buttons, filter rows, Clear all).
  useEffect(() => {
    const value = textFilterValue(filterState);
    setSearchText((prev) => (prev === value ? prev : value));
  }, [filterState]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
    setFilterState((prev) => withTextFilter(prev, value.trim()));
  }, []);

  const submitSearch = useCallback((event: React.FormEvent) => {
    event.preventDefault();
    setAppliedFilterState(filterState);
  }, [filterState]);

  // Keep the displayed rows under the submitted sort until Search is clicked;
  // draft filter-bar changes must not make stale results look newly queried.
  const sortCol = appliedFilterState.orderBy;
  const sortDir = appliedFilterState.orderDir;
  const handleSort = useCallback((col: string) => {
    setFilterState((prev) => ({
      ...prev,
      orderBy: col,
      orderDir: prev.orderBy === col ? (prev.orderDir === 'asc' ? 'desc' : 'asc') : 'desc',
    }));
  }, []);
  const sortedStories = useMemo(
    () => sortByColumn(stories as unknown as Record<string, unknown>[], sortCol, sortDir) as unknown as AdminStory[],
    [stories, sortCol, sortDir],
  );

  const atLimit = stories.length >= RESULT_LIMIT;
  const hasUnsubmittedChanges = filterStateToParams(filterState, defaultFilterState).toString()
    !== filterStateToParams(appliedFilterState, defaultFilterState).toString();
  const countLabel = loading
    ? (stories.length > 0 ? 'Updating…' : 'Loading…')
    : `${stories.length}${atLimit ? '+' : ''} articles${atLimit ? ` (showing first ${RESULT_LIMIT})` : ''}`;

  const content = (
    <>
      <StatusMessage message={status.message} isError={status.isError} />
      <section className="admin-panel">
        <div className="admin-panel-header">
          <h2>
            Articles
            {admin && (
              <button
                type="button"
                className="add-article-btn"
                onClick={() => document.dispatchEvent(new Event('open-add-link-modal'))}
                title="Add article"
              >+</button>
            )}
          </h2>
          <span>{countLabel}</span>
        </div>
        <form className="article-search-form" onSubmit={submitSearch}>
          <div className="search-input-wrap">
            <input
              ref={searchInputRef}
              className="search-input"
              type="text"
              placeholder="Search title, summary, domain, URL, category…"
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            <button
              className="search-submit-button"
              type="submit"
              disabled={loading && !hasUnsubmittedChanges}
            >
              {loading && !hasUnsubmittedChanges ? 'Searching…' : 'Search'}
            </button>
          </div>
          <ArticleFilterBar state={filterState} config={filterConfig} onChange={setFilterState} />
          <div className="search-query-summary">
            {hasUnsubmittedChanges ? 'Press Search to apply: ' : ''}
            {describeSearch(filterState, filterConfig)}
          </div>
        </form>
        {stories.length === 0 ? (
          <div className="empty-panel">{loading ? 'Loading articles…' : 'No articles found.'}</div>
        ) : (
          <div className="articles-table-wrap">
            {admin && (
              <div className="table-options-row">
                <label className="breakdown-toggle">
                  <input type="checkbox" checked={showBreakdown} onChange={toggleBreakdown} />
                  Score breakdown columns
                </label>
                {showBreakdown && (
                  <details className="column-legend">
                    <summary>Column legend</summary>
                    <div className="column-legend-items">
                      <span><strong>Scr</strong> — Overall score</span>
                      <span><strong>T</strong> — Tier (1–5)</span>
                      <span><strong>Top</strong> — Topic boost</span>
                      <span><strong>Src</strong> — Source/site quality adjustment</span>
                      <span><strong>Soc</strong> — Social-signal adjustment</span>
                      <span><strong>Gt</strong> — Gated-content penalty</span>
                      <span><strong>Adj</strong> — Score adjustments (redundancy penalty for articles that add nothing beyond existing coverage; staleness penalty for rehashes of old news)</span>
                      <span><strong>Dcy</strong> — Time-decay multiplier</span>
                      <span><strong>Ftch</strong> — Content fetched</span>
                      <span><strong>Gtd</strong> — Gated (paywall)</span>
                    </div>
                  </details>
                )}
              </div>
            )}
            <table className="articles-table">
              <thead>
                <tr>
                  <th title="Links — Internal / external / feed / copy">Links</th>
                  <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="title" tooltip="Title — Article title">Title</SortTh>
                  <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="canonical_domain" tooltip="Site — Source domain">Site</SortTh>
                  {admin && <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="topic" tooltip="Topic — Assigned topic">Topic</SortTh>}
                  {admin && <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="tldr_relevance_score" tooltip="TLDR — Latest relevance score against the main TLDR feed (— = never scored)">TLDR</SortTh>}
                  {admin && showBreakdown && <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="topic_boost" tooltip="Top — Topic boost">Top</SortTh>}
                  {admin && showBreakdown && <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="source_adjustment" tooltip="Src — Source/site quality adjustment">Src</SortTh>}
                  {admin && showBreakdown && <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="social_boost" tooltip="Soc — Social-signal adjustment">Soc</SortTh>}
                  {admin && showBreakdown && <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="gated_penalty" tooltip="Gt — Gated-content penalty">Gt</SortTh>}
                  {admin && showBreakdown && <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="adj_penalty" tooltip="Adj — Score adjustments (redundancy + staleness penalties)">Adj</SortTh>}
                  {admin && showBreakdown && <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="time_decay" tooltip="Dcy — Time-decay multiplier">Dcy</SortTh>}
                  <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="published_at" tooltip="Published — Article publication time">Published</SortTh>
                  {admin && <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="created_at" tooltip="Ingested — When Kingfisher first saw the article">Ingested</SortTh>}
                  {admin && showBreakdown && <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="has_content" className="col-bool-header" tooltip="Ftch — Content fetched">Ftch</SortTh>}
                  {admin && showBreakdown && <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="is_gated" className="col-bool-header" tooltip="Gtd — Gated (paywall)">Gtd</SortTh>}
                  {!admin && <SortTh sortCol={sortCol} sortDir={sortDir} onSort={handleSort} col="tier" tooltip="Score — Tier (1–5)">Score</SortTh>}
                </tr>
              </thead>
              <tbody>
                {sortedStories.map((story) => (
                  <ArticleRow key={story.id} story={story} admin={admin} showBreakdown={showBreakdown} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );

  if (hideShell) return content;

  return (
    <div className="reader-shell">
      <ReaderNavbar
        selectedChannelId=""
        onSelectChannel={handleSelectChannel}
        user={user}
        onLogout={logout}
      />
      {content}
    </div>
  );
}

function ArticleRow({ story, admin, showBreakdown }: { story: AdminStory; admin: boolean; showBreakdown: boolean }) {
  const [copyState, setCopyState] = useState<'idle' | 'copying' | 'done' | 'none'>('idle');
  const title = decodeHtmlEntities(story.title) || '(untitled)';

  const handleCopy = async () => {
    setCopyState('copying');
    try {
      const detail = await adminStoryRepository.getStory(story.id);
      if (detail.content) {
        await navigator.clipboard.writeText(detail.content);
        setCopyState('done');
        setTimeout(() => setCopyState('idle'), 1500);
      } else {
        setCopyState('none');
        setTimeout(() => setCopyState('idle'), 1500);
      }
    } catch {
      setCopyState('idle');
    }
  };

  const titleLink = admin ? `/admin/article/${story.id}` : `/read/${story.id}`;
  const domainLink = domainSearchUrl(story.canonical_domain || '');

  const fmtNum = (v: unknown) => typeof v === 'number' ? v.toFixed(2) : '—';
  const fmtNonZero = (v: unknown) => typeof v === 'number' && v !== 0 ? v.toFixed(2) : '';

  return (
    <tr>
        <td className="col-links">
          <div className="article-links">
            {admin && <ScoreBadge story={story} />}
            {admin && <Link to={`/admin/article/${story.id}`} title="Internal article page">↳</Link>}
            {story.url ? (
              <a href={safeHref(story.url)} target="_blank" rel="noopener noreferrer" title="External article">↗</a>
            ) : <span className="article-link-disabled">↗</span>}
            {admin && (story.feed_url ? (
              <a href={safeHref(story.feed_url)} target="_blank" rel="noopener noreferrer" title="RSS feed">⊕</a>
            ) : <span className="article-link-disabled">⊕</span>)}
            {admin && (
              <button
                className="article-link-copy"
                onClick={handleCopy}
                disabled={copyState === 'copying'}
                title="Copy full content"
              >
                {copyState === 'done' ? '✓' : copyState === 'none' ? '∅' : '⎘'}
              </button>
            )}
          </div>
        </td>
        <td className="col-title">
          <Link to={titleLink} title={title}>
            {title}
          </Link>
        </td>
        <td className="col-site">
          {story.canonical_domain ? (
            <Link
              to={domainLink}
              className="story-domain-link"
              title={story.canonical_domain}
            >
              {truncate(story.canonical_domain, 22)}
            </Link>
          ) : '—'}
        </td>
        {admin && <td className="col-topic" title={story.topic || ''} onClick={e => e.currentTarget.classList.toggle('expanded')}>{story.topic || '—'}</td>}
        {admin && <TldrRelevanceCell story={story} />}
        {admin && showBreakdown && <td className="col-score">{fmtNum(story.topic_boost)}</td>}
        {admin && showBreakdown && <td className="col-score">{fmtNonZero(story.source_adjustment)}</td>}
        {admin && showBreakdown && <td className="col-score">{fmtNonZero(readSocialComponent(story))}</td>}
        {admin && showBreakdown && <td className="col-score">{(story.gated_penalty ?? 0) > 0 ? fmtNum(story.gated_penalty) : ''}</td>}
        {admin && showBreakdown && <td className="col-score">{(story.adj_penalty ?? 0) > 0 ? fmtNum(story.adj_penalty) : ''}</td>}
        {admin && showBreakdown && <td className="col-score">{fmtNum(story.time_decay)}</td>}
        <td className="col-date">{formatDateTime(story.published_at)}</td>
        {admin && <td className="col-date">{formatDateTime(story.created_at)}</td>}
        {admin && showBreakdown && <td className="col-bool">{story.has_content ? '✓' : ''}</td>}
        {admin && showBreakdown && <td className="col-bool">{story.is_gated ? '✓' : ''}</td>}
        {!admin && <td className="col-tier">{story.tier ?? '—'}</td>}
    </tr>
  );
}

// Latest relevance judgment of this article against the main TLDR feed rule.
// Three states: never judged, judged with a numeric score, and judged with a
// bare relevant/irrelevant verdict (older judgments lack the numeric score).
function TldrRelevanceCell({ story }: { story: AdminStory }) {
  if (story.tldr_relevant == null) {
    return <td className="col-score" title="Never scored for the TLDR feed">—</td>;
  }
  const verdict = story.tldr_relevant ? 'relevant' : 'not relevant';
  const hasScore = typeof story.tldr_relevance_score === 'number';
  return (
    <td
      className="col-score"
      title={`Judged ${verdict} for the TLDR feed${hasScore ? '' : ' (no numeric score)'}`}
    >
      {hasScore ? story.tldr_relevance_score!.toFixed(2) : story.tldr_relevant ? '✓' : '✗'}
    </td>
  );
}

// Adapt an admin detail payload into the StoryElement shape the shared
// ScoreBreakdownModal reads. Only the score-relevant fields matter; the
// feed-specific rows (relevance, feed score) simply stay absent here.
function detailToStoryElement(story: AdminStory, detail: AdminStoryDetail): StoryElement {
  return {
    id: detail.id,
    title: detail.title ?? story.title ?? '',
    is_read: false,
    is_flagged: false,
    canonical_domain: detail.canonical_domain ?? story.canonical_domain,
    tier: detail.tier ?? story.tier,
    base_score: detail.base_score ?? null,
    base_score_details: detail.base_score_details ?? null,
    canonical_score: detail.canonical_score ?? null,
  };
}

function ScoreBadge({ story }: { story: AdminStory }) {
  const [detail, setDetail] = useState<AdminStoryDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // base_score (the overall score) is in the filter response when scored;
  // otherwise it's loaded with the breakdown on first click.
  const score = detail ? detail.base_score : story.overall_score;
  const label = loading ? '…' : typeof score === 'number' ? score.toFixed(1) : 'S';

  const open = async () => {
    if (!detail && !loading) {
      setLoading(true);
      try {
        setDetail(await adminStoryRepository.getStory(story.id));
      } catch {
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    setModalOpen(true);
  };

  return (
    <>
      <button
        type="button"
        className="score-badge score-badge-load"
        onClick={open}
        disabled={loading}
        title="Show score breakdown"
      >
        {label}
      </button>
      {modalOpen && detail && (
        <ScoreBreakdownModal
          story={detailToStoryElement(story, detail)}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}
